import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowUpRight, Menu, X, ChevronDown } from 'lucide-react';

export const Navigation = ({ variant = 'dark' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ueberDropdownOpen, setUeberDropdownOpen] = useState(false);
  const [mobileUeberExpanded, setMobileUeberExpanded] = useState(false);
  const dropdownRef = useRef(null);

  const isDark = variant === 'dark';

  const isActive = (path) => location.pathname === path;
  const isUeberActive = () => ['/ueber-mich', '/philosophie'].includes(location.pathname);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUeberDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainNavLinks = [
    { label: 'Absicherungs-Check', path: '/absicherungscheck' },
    { label: 'Renten-Check', path: '/rentencheck' },
    { label: 'Kontakt', path: '/kontakt' },
  ];

  const ueberSubLinks = [
    { label: 'Über mich', path: '/ueber-mich' },
    { label: 'Philosophie', path: '/philosophie' },
  ];

  const isZusammenarbeitActive = () => location.pathname === '/zusammenarbeit';
  const isStrategiefelderActive = () => location.pathname === '/strategiefelder';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${
      isDark 
        ? 'bg-[#151b2d]/95 backdrop-blur-xl border-b border-white/5' 
        : 'bg-white/95 backdrop-blur-xl border-b border-black/5'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-4">
        {/* Desktop Layout - show on lg screens and up (1024px+) */}
        <div className="hidden lg:flex items-center justify-between">
          {/* Left - Name & Positioning stacked */}
          <button
            onClick={() => navigate('/')}
            className="text-left"
          >
            <span className={`font-playfair text-xl font-bold tracking-tight block ${
              isDark ? 'text-white hover:text-white/80' : 'text-navy hover:text-charcoal'
            }`}>
              Nico Helfrich
            </span>
            <span className={`text-[10px] font-medium tracking-widest uppercase ${
              isDark ? 'text-white/40' : 'text-slate/50'
            }`}>
              Ingenieurdenken für deine Absicherung
            </span>
          </button>

          {/* Center - Nav Links */}
          <div className="flex items-center gap-4 xl:gap-5">
            {mainNavLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    isDark ? 'text-white/60 hover:text-white' : 'text-slate hover:text-navy'
                  }`}
                >
                  {link.label}
                </a>
              ) : (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? isDark ? 'text-white' : 'text-navy'
                      : isDark ? 'text-white/60 hover:text-white' : 'text-slate hover:text-navy'
                  }`}
                >
                  {link.label}
                  {link.showArrow && <ArrowUpRight className="w-3.5 h-3.5" />}
                </button>
              )
            ))}

            {/* Über Dropdown */}
            <div 
              ref={dropdownRef}
              className="relative group"
              onMouseEnter={() => setUeberDropdownOpen(true)}
              onMouseLeave={() => setUeberDropdownOpen(false)}
            >
              <button
                onClick={() => navigate('/ueber-mich')}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors py-2 ${
                  isUeberActive()
                    ? isDark ? 'text-white' : 'text-navy'
                    : isDark ? 'text-white/60 hover:text-white' : 'text-slate hover:text-navy'
                }`}
              >
                Über
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${ueberDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu - with invisible bridge to prevent hover gap */}
              <div className={`absolute top-full left-0 pt-1 ${ueberDropdownOpen ? 'block' : 'hidden'}`}>
                <div className={`py-2 min-w-[160px] rounded-xl shadow-xl ${
                  isDark 
                    ? 'bg-[#1a2235] border border-white/10' 
                    : 'bg-white border border-black/5'
                }`}>
                  {ueberSubLinks.map((link) => (
                    <button
                      key={link.path}
                      onClick={() => {
                        navigate(link.path);
                        setUeberDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                        isActive(link.path)
                          ? isDark ? 'text-white bg-white/10' : 'text-navy bg-navy/5'
                          : isDark ? 'text-white/70 hover:text-white hover:bg-white/5' : 'text-slate hover:text-navy hover:bg-black/5'
                      }`}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Strategiefelder Link */}
            <button
              onClick={() => navigate('/strategiefelder')}
              className={`text-sm font-medium transition-colors ${
                isStrategiefelderActive()
                  ? isDark ? 'text-white' : 'text-navy'
                  : isDark ? 'text-white/60 hover:text-white' : 'text-slate hover:text-navy'
              }`}
            >
              Strategiefelder
            </button>
          </div>


          {/* Right - CTA Button */}
          <div className="flex justify-end">
            <button
              onClick={() => navigate('/strategiegespraech')}
              className="px-5 py-2.5 rounded-full bg-emerald/90 text-white text-sm font-medium hover:bg-emerald transition-all duration-300 shadow-lg shadow-emerald/20"
              data-testid="nav-booking-btn"
            >
              Strategiegespräch
            </button>
          </div>
        </div>

        {/* Mobile Layout - show on screens smaller than lg */}
        <div className="lg:hidden flex items-center justify-between">
          {/* Left - Name */}
          <button
            onClick={() => navigate('/')}
            className={`font-playfair text-xl font-bold tracking-tight transition-colors ${
              isDark ? 'text-white hover:text-white/80' : 'text-navy hover:text-charcoal'
            }`}
          >
            Nico Helfrich
          </button>

          {/* Right - CTA & Menu */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/strategiegespraech')}
              className="px-4 py-2 rounded-full bg-emerald/90 text-white text-sm font-medium hover:bg-emerald transition-all duration-300 shadow-lg shadow-emerald/20"
              data-testid="nav-booking-btn-mobile"
            >
              Termin
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-slate hover:text-navy hover:bg-black/5'
              }`}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`lg:hidden mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`}>
            {/* Mobile Positioning Statement */}
            <div className={`text-xs font-medium tracking-wide mb-4 ${
              isDark ? 'text-white/30' : 'text-slate/50'
            }`}>
              Ingenieurdenken für deine Absicherung
            </div>
            
            <div className="flex flex-col gap-2">
              {/* Main Nav Links */}
              {mainNavLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center justify-between py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                      isDark ? 'text-white/70 hover:bg-white/5' : 'text-charcoal hover:bg-black/5'
                    }`}
                  >
                    {link.label}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button
                    key={link.path}
                    onClick={() => {
                      navigate(link.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center justify-between py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? isDark ? 'bg-white/10 text-white' : 'bg-navy/5 text-navy'
                        : isDark ? 'text-white/70 hover:bg-white/5' : 'text-charcoal hover:bg-black/5'
                    }`}
                  >
                    {link.label}
                    {link.showArrow && <ArrowUpRight className="w-4 h-4" />}
                  </button>
                )
              ))}

              {/* Über Section (Expandable) */}
              <div>
                <button
                  onClick={() => setMobileUeberExpanded(!mobileUeberExpanded)}
                  className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                    isUeberActive()
                      ? isDark ? 'bg-white/10 text-white' : 'bg-navy/5 text-navy'
                      : isDark ? 'text-white/70 hover:bg-white/5' : 'text-charcoal hover:bg-black/5'
                  }`}
                >
                  Über
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileUeberExpanded ? 'rotate-180' : ''}`} />
                </button>

                {/* Sub Links */}
                {mobileUeberExpanded && (
                  <div className="mt-1 ml-4 flex flex-col gap-1">
                    {ueberSubLinks.map((link) => (
                      <button
                        key={link.path}
                        onClick={() => {
                          navigate(link.path);
                          setMobileMenuOpen(false);
                          setMobileUeberExpanded(false);
                        }}
                        className={`py-2.5 px-4 rounded-lg text-sm font-medium text-left transition-colors ${
                          isActive(link.path)
                            ? isDark ? 'bg-white/10 text-white' : 'bg-navy/5 text-navy'
                            : isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-slate hover:text-navy hover:bg-black/5'
                        }`}
                      >
                        {link.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Strategiefelder Link */}
              <button
                onClick={() => {
                  navigate('/strategiefelder');
                  setMobileMenuOpen(false);
                }}
                className={`flex items-center justify-between py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                  isStrategiefelderActive()
                    ? isDark ? 'bg-white/10 text-white' : 'bg-navy/5 text-navy'
                    : isDark ? 'text-white/70 hover:bg-white/5' : 'text-charcoal hover:bg-black/5'
                }`}
              >
                Strategiefelder
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
